/**
 * CampusOverview
 *
 * Main container component for the 3D campus overview.
 * Displays when currentLocation is CAMPUS_OVERVIEW.
 * Provides navigation to different campus buildings,
 * HUD with company stats, and first-time onboarding.
 */

import React, { useCallback, useState } from 'react';
import { CampusLocation } from '../../../types/spatial';
import { useSpatialNavigation, useBuildingSelection } from '../../../hooks/useSpatialNavigation';
import { getAllBuildingConfigs, getLocationConfig } from '../../../config/locationConfig';
import { LocationProvider } from '../LocationContext';
import { CampusScene } from './CampusScene';
import { BuildingInfoPanel } from './BuildingInfoPanel';
import { CampusHUD } from './CampusHUD';

export const CampusOverview: React.FC = () => {
  const { navigateTo, unlockedLocations, isUnlocked } = useSpatialNavigation();
  const { selectedBuilding, selectBuilding, hoverBuilding } = useBuildingSelection();
  const [hoveredLocation, setHoveredLocation] = useState<CampusLocation | null>(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    return localStorage.getItem('campus-onboarding-dismissed') === 'true';
  });

  const buildings = getAllBuildingConfigs();

  const handleBuildingClick = useCallback(
    (location: CampusLocation) => {
      if (isUnlocked(location)) {
        // Auto-dismiss onboarding on first building entry
        if (!onboardingDismissed) {
          setOnboardingDismissed(true);
          localStorage.setItem('campus-onboarding-dismissed', 'true');
        }
        navigateTo(location);
      } else {
        selectBuilding(location);
      }
    },
    [isUnlocked, navigateTo, selectBuilding, onboardingDismissed]
  );

  const handleBuildingHover = useCallback(
    (location: CampusLocation | null) => {
      setHoveredLocation(location);
      hoverBuilding(location);
      if (location) {
        selectBuilding(location);
      }
    },
    [hoverBuilding, selectBuilding]
  );

  const handleEnterBuilding = useCallback(() => {
    if (selectedBuilding.location && isUnlocked(selectedBuilding.location)) {
      navigateTo(selectedBuilding.location);
    }
  }, [selectedBuilding.location, isUnlocked, navigateTo]);

  const selectedConfig = selectedBuilding.location
    ? getLocationConfig(selectedBuilding.location)
    : null;

  // Campus overview renders WITHOUT LocationWrapper to avoid flex-1/overflow-auto
  // constraints that prevent the 3D canvas from filling the full viewport.
  // LocationProvider is kept for context compatibility.
  return (
    <LocationProvider location={CampusLocation.CAMPUS_OVERVIEW}>
      <div
        className="absolute inset-0"
        data-testid="campus-overview"
        data-location={CampusLocation.CAMPUS_OVERVIEW}
      >
        {/* 3D Scene -- fills entire container */}
        <CampusScene
          buildings={buildings}
          unlockedLocations={unlockedLocations}
          selectedLocation={selectedBuilding.location}
          hoveredLocation={hoveredLocation}
          onBuildingClick={handleBuildingClick}
          onBuildingHover={handleBuildingHover}
        />

        {/* Company Stats HUD + Onboarding */}
        <CampusHUD
          onboardingDismissed={onboardingDismissed}
          onDismissOnboarding={() => {
            setOnboardingDismissed(true);
            localStorage.setItem('campus-onboarding-dismissed', 'true');
          }}
        />

        {/* Building Info Panel */}
        {selectedConfig && (
          <BuildingInfoPanel
            config={selectedConfig}
            isUnlocked={isUnlocked(selectedConfig.id)}
            onEnter={handleEnterBuilding}
          />
        )}
      </div>
    </LocationProvider>
  );
};

export default CampusOverview;
